'use client';

import {
  createContext,
  useContext,
  useCallback,
  type ReactNode,
  useMemo,
} from 'react';
import {useGetActiveScreenId} from '@features/activeScreenIdContext/useGetActiveScreenId';
import type {XYPosition} from '@xyflow/react';
import {useReactFlow} from '@xyflow/react';
import {trpc, type TrpcUtils} from '@features/utils/trpc';
import {
  buildScreenBounds,
  getViewportCenterInFlowCoordinates,
  putInFree,
  type Direction,
  putInViewportCenter,
  putAtDirection,
} from '@features/flow/utils/screenPositioning';
import amplitudeService from '@features/analytics/amplitudeService';
import {useAddScreenToFlow} from '@features/flow/flowHook';
import {hiddenMessageFromPrevScreen} from '../../chat/chatSelector';
import {useSetAtom} from 'jotai';
import {useViewport} from '../components/useViewport';
import {useSetActiveScreenId} from '@features/activeScreenIdContext/useSetActiveScreenId';
import {generateId} from '@features/db/generateId';
import {FlowContext} from '@features/contexts/FlowContext';
import type {GenType} from '@features/flow/flowService';
import {
  getNodeDimensions,
  withComponentDimensions,
} from '@features/flow/utils/nodeDimensions';
import type {DesignComponent} from '@features/designComponents/types';
import type {WebStructureSchema} from '@features/designComponents/webComponentSchema';
import {setStreamingGenTypeAtom} from '@features/ai/streaming/streamingGenTypeAtom';
import {getEffectiveGenType} from '@features/server/utils/getEffectiveGenType';
import {
  resolveThemeForCenteredScreen,
  resolveThemeForRelativeScreen,
} from './resolveScreenTheme';
import {invalidateScreenCaches} from '../utils/invalidateScreenCaches';

type CreateScreenOptions = {
  hiddenMessage?: string;
  themeId?: string;
};

/**
 * Build screen structure string from raw structure data.
 * This is extracted from useScreenStructure/useScreenStructureForScreen
 * to allow imperative (non-reactive) usage.
 */
function buildScreenStructureString(
  structure: Awaited<
    ReturnType<TrpcUtils['screens']['getActiveStructure']['fetch']>
  >,
  genType: GenType,
): string | undefined {
  if (genType === 'web-html-css' || genType === 'mobile-html-css') {
    return structure?.htmlCss ?? undefined;
  }

  if (!structure?.legacyComponents) {
    return undefined;
  }

  const legacy = structure.legacyComponents as WebStructureSchema;
  const designComponentsStructure = legacy.at(0);

  const currentScreenStructure = designComponentsStructure?.map(
    (item: DesignComponent) => ({
      ...item,
      // deprecated code
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      props: {...item.props, iconsSvg: [], images: []},
    }),
  );

  return JSON.stringify(currentScreenStructure);
}

/**
 * Build hidden message from recent messages and screen structure.
 * This is extracted from useHiddenMessage to allow imperative usage.
 */
function buildHiddenMessage(
  recentUserMessages: {text: string | null}[] | undefined,
  screenStructure: string | undefined,
): string {
  const messagesWithText = recentUserMessages?.filter(
    (msg): msg is {text: string} => msg.text !== null,
  );
  const oldMessages = messagesWithText?.length
    ? `Use these previous user's messages: ${messagesWithText
        .map((msg) => msg.text)
        .join(', ')}. `
    : '';

  return `${oldMessages ? `<userPreviousMessages>${oldMessages}</userPreviousMessages>` : ''}
        ${screenStructure ? ` <screenThatNeedsToBeUpdated>${screenStructure}</screenThatNeedsToBeUpdated>` : ''}`;
}

type ComponentScreenCopyOptions = {
  sourceScreenId: string;
  displayNameSuffix?: string;
  filenameSuffix?: string;
  /**
   * Override the new screen's screenSize. Defaults to the source screen's
   * screenSize. Used by platform-conversion flows to size the canvas node
   * correctly from creation time, before the LLM finishes writing the file.
   */
  screenSize?: 'desktop' | 'mobile';
};

type ScreenCreationContextType = {
  /**
   * Create a new screen at an absolute position
   */
  createScreen: (
    x: number,
    y: number,
    options?: CreateScreenOptions,
  ) => Promise<string>;
  /**
   * Create a new screen relative to an existing screen.
   * Automatically fetches the hidden message from the source screen.
   * Finds the first available position that doesn't overlap with existing screens.
   * @param screenId - The screen to position relative to
   * @param direction - Where to place the new screen
   * @param copyOptions - For component flows: copy source screen content instead of creating empty
   */
  createScreenRelativeTo: (
    screenId: string,
    direction: Direction,
    copyOptions?: ComponentScreenCopyOptions,
  ) => Promise<string>;
  /**
   * Create a new screen relative to the currently active screen.
   * Uses activeScreenId from context.
   * Finds the first available position that doesn't overlap with existing screens.
   * @param direction - Where to place the new screen (default: 'right')
   */
  createScreenContextual: (direction?: Direction) => Promise<string>;
  /**
   * Create a new screen in the center of the viewport (for sidebar button).
   * If the center position overlaps with existing screens, finds the first
   * available position to the right.
   */
  createScreenCentered: (options?: CreateScreenOptions) => Promise<string>;
  createScreenInFreeSpace: (options?: CreateScreenOptions) => Promise<string>;
};

const ScreenCreationContext = createContext<ScreenCreationContextType | null>(
  null,
);

export function useScreenCreationContext() {
  const context = useContext(ScreenCreationContext);
  if (!context) {
    throw new Error(
      'useScreenCreationContext must be used within ScreenCreationProvider',
    );
  }
  return context;
}

type ScreenCreationProviderProps = {
  flowId: string;
  children: ReactNode;
};

export function ScreenCreationProvider({
  flowId,
  children,
}: ScreenCreationProviderProps) {
  const {genType, themeId: flowThemeId} = useContext(FlowContext).flow;
  const isComponents = genType === 'components';
  const getActiveScreenId = useGetActiveScreenId();
  const {screenToFlowPosition, getNode} = useReactFlow();
  const {data: screens} = trpc.screens.getFlowScreens.useQuery({flowId});
  const screensWithDimensions = useMemo(
    () => withComponentDimensions(screens ?? []),
    [screens],
  );
  const trpcUtils = trpc.useUtils();
  const addScreenToFlow = useAddScreenToFlow();
  const copyComponentScreen = trpc.screens.copyComponentScreen.useMutation();
  const setHiddenMessage = useSetAtom(hiddenMessageFromPrevScreen);
  const setStreamingGenType = useSetAtom(setStreamingGenTypeAtom);
  const {centerOnPosition} = useViewport();
  const setSelectedChatId = useSetActiveScreenId();

  const getViewportCenter = useCallback(
    () =>
      getViewportCenterInFlowCoordinates({
        screenToFlowPosition,
      }),
    [screenToFlowPosition],
  );

  const getExistingScreenBounds = useCallback(
    () =>
      buildScreenBounds({
        screens: screensWithDimensions,
        flowGenType: genType,
        getNode,
        screenToFlowPosition,
      }),
    [screensWithDimensions, genType, getNode, screenToFlowPosition],
  );

  const copyComponentScreenMutateAsync = copyComponentScreen.mutateAsync;
  const createComponentScreen = useCallback(async (): Promise<string> => {
    amplitudeService.logEvent('[Flow] add new component screen click');
    const {screenId} = await copyComponentScreenMutateAsync({flowId});
    await invalidateScreenCaches(trpcUtils, flowId);
    setSelectedChatId(screenId);
    return screenId;
  }, [flowId, copyComponentScreenMutateAsync, setSelectedChatId, trpcUtils]);

  const createComponentScreenCentered =
    useCallback(async (): Promise<string> => {
      amplitudeService.logEvent('[Flow] add new component screen click');

      const viewportCenter = getViewportCenter();
      const {width, height} = getNodeDimensions(genType);
      const position = putInViewportCenter(viewportCenter, {
        width,
        height,
      });

      const {screenId} = await copyComponentScreenMutateAsync({
        flowId,
        x: position.x,
        y: position.y,
      });
      await invalidateScreenCaches(trpcUtils, flowId);
      setSelectedChatId(screenId);
      void centerOnPosition(position.x, position.y, genType);

      return screenId;
    }, [
      genType,
      flowId,
      copyComponentScreenMutateAsync,
      trpcUtils,
      setSelectedChatId,
      centerOnPosition,
      getViewportCenter,
    ]);

  const createComponentScreenInFreeSpace =
    useCallback(async (): Promise<string> => {
      amplitudeService.logEvent('[Flow] add new component screen click');

      const viewportCenter = getViewportCenter();
      const existingScreens = getExistingScreenBounds();
      const {width, height} = getNodeDimensions(genType);
      const centeredPosition = putInViewportCenter(viewportCenter, {
        width,
        height,
      });
      const position = putInFree(existingScreens, centeredPosition, {
        width,
        height,
      });

      const {screenId} = await copyComponentScreenMutateAsync({
        flowId,
        x: position.x,
        y: position.y,
      });
      await invalidateScreenCaches(trpcUtils, flowId);
      setSelectedChatId(screenId);
      void centerOnPosition(position.x, position.y, genType);

      return screenId;
    }, [
      genType,
      flowId,
      copyComponentScreenMutateAsync,
      trpcUtils,
      setSelectedChatId,
      centerOnPosition,
      getViewportCenter,
      getExistingScreenBounds,
    ]);

  const createComponentScreenRelativeTo = useCallback(
    async (
      referenceScreenId: string,
      direction: Direction,
      copyOptions?: ComponentScreenCopyOptions,
    ): Promise<string> => {
      amplitudeService.logEvent('[Flow] add new component screen click');

      const sourceScreen = screensWithDimensions.find(
        (s) => s.id === referenceScreenId,
      );
      const existingScreens = getExistingScreenBounds();
      const sourceBounds = existingScreens.find(
        (s) => s.id === referenceScreenId,
      );

      const fallbackSize = getNodeDimensions(
        sourceScreen?.screenSize ?? genType,
      );
      let position: XYPosition;
      if (sourceBounds) {
        position = putAtDirection(
          existingScreens,
          sourceBounds,
          {
            width: sourceBounds.width,
            height: sourceBounds.height,
          },
          direction,
        );
      } else {
        console.warn(
          `[ScreenCreation] Warning: Could not find bounds for reference screen ${referenceScreenId}. Falling back to free space positioning.`,
        );
        position = putInFree(
          existingScreens,
          putInViewportCenter(getViewportCenter(), {
            width: fallbackSize.width,
            height: fallbackSize.height,
          }),
          fallbackSize,
        );
      }

      const targetScreenSize =
        copyOptions?.screenSize ?? sourceScreen?.screenSize ?? undefined;
      const {screenId} = await copyComponentScreenMutateAsync({
        flowId,
        x: position.x,
        y: position.y,
        sourceScreenId: copyOptions?.sourceScreenId,
        displayNameSuffix: copyOptions?.displayNameSuffix,
        filenameSuffix: copyOptions?.filenameSuffix,
        screenSize: targetScreenSize,
      });
      await invalidateScreenCaches(trpcUtils, flowId);
      setSelectedChatId(screenId);
      void centerOnPosition(
        position.x,
        position.y,
        targetScreenSize ?? genType,
      );

      return screenId;
    },
    [
      genType,
      flowId,
      copyComponentScreenMutateAsync,
      trpcUtils,
      setSelectedChatId,
      centerOnPosition,
      getViewportCenter,
      getExistingScreenBounds,
      screensWithDimensions,
    ],
  );

  const createScreen = useCallback(
    async (
      x: number,
      y: number,
      options?: CreateScreenOptions,
    ): Promise<string> => {
      amplitudeService.logEvent('[Flow] add new screen click');

      if (options?.hiddenMessage) {
        setHiddenMessage(options.hiddenMessage);
      }

      const screenId = generateId();

      // Set the selected screen immediately - no need to wait
      setSelectedChatId(screenId);

      const screenMutation = addScreenToFlow.mutateAsync({
        flowId,
        screenId,
        x,
        y,
        themeId: options?.themeId,
      });

      // Center viewport on the new screen position immediately.
      // Using coordinates directly avoids timing issues with fitView
      // (which requires the node to exist in ReactFlow's registry).
      void centerOnPosition(x, y, genType);

      await screenMutation;

      return screenId;
    },
    [
      flowId,
      addScreenToFlow,
      setHiddenMessage,
      centerOnPosition,
      setSelectedChatId,
      genType,
    ],
  );

  /**
   * Fetch hidden message data lazily (on demand) without subscribing to queries.
   * This prevents re-renders in components that use this context.
   */
  const fetchHiddenMessageForScreen = useCallback(
    async (screenId: string): Promise<string> => {
      const [recentUserMessages, structure] = await Promise.all([
        trpcUtils.screens.recentUserMessages.fetch({screenId}),
        trpcUtils.screens.getActiveStructure.fetch({screenId}),
      ]);

      const screenStructure = buildScreenStructureString(structure, genType);
      return buildHiddenMessage(recentUserMessages, screenStructure);
    },
    [trpcUtils, genType],
  );

  const createScreenCentered = useCallback(
    async (options?: CreateScreenOptions): Promise<string> => {
      const viewportCenter = getViewportCenter();
      const {width, height} = getNodeDimensions(genType);
      const position = putInViewportCenter(viewportCenter, {
        width,
        height,
      });

      const themeId = resolveThemeForCenteredScreen(
        options?.themeId,
        getActiveScreenId(),
        screensWithDimensions,
        flowThemeId,
      );

      return createScreen(position.x, position.y, {...options, themeId});
    },
    [
      genType,
      createScreen,
      getActiveScreenId,
      flowThemeId,
      getViewportCenter,
      screensWithDimensions,
    ],
  );

  const createScreenInFreeSpace = useCallback(
    async (options?: CreateScreenOptions): Promise<string> => {
      const viewportCenter = getViewportCenter();
      const existingScreens = getExistingScreenBounds();
      const {width, height} = getNodeDimensions(genType);
      const centeredPosition = putInViewportCenter(viewportCenter, {
        width,
        height,
      });
      const position = putInFree(existingScreens, centeredPosition, {
        width,
        height,
      });

      const themeId = resolveThemeForCenteredScreen(
        options?.themeId,
        getActiveScreenId(),
        screensWithDimensions,
        flowThemeId,
      );

      return createScreen(position.x, position.y, {...options, themeId});
    },
    [
      getViewportCenter,
      getExistingScreenBounds,
      genType,
      getActiveScreenId,
      screensWithDimensions,
      flowThemeId,
      createScreen,
    ],
  );

  const createScreenRelativeTo = useCallback(
    async (screenId: string, direction: Direction): Promise<string> => {
      // Fetch hidden message and source structure lazily
      const [hiddenMessage, sourceStructure] = await Promise.all([
        fetchHiddenMessageForScreen(screenId),
        trpcUtils.screens.getActiveStructure.fetch({screenId}),
      ]);

      const existingScreens = screensWithDimensions;
      const existingBounds = getExistingScreenBounds();

      const sourceScreen = existingScreens.find((s) => s.id === screenId);
      const sourceBounds = existingBounds.find((s) => s.id === screenId);
      const screenOptions: CreateScreenOptions = {
        hiddenMessage,
        themeId: resolveThemeForRelativeScreen(
          sourceStructure?.themeId,
          sourceScreen?.themeId,
        ),
      };

      // Inherit genType from source structure so the new adjacent screen renders
      // with the same platform as its neighbor while generation state is streaming.
      const sourceGenType = getEffectiveGenType(
        sourceStructure?.genType,
        null, // No streaming genType for adjacent screens
        genType,
      );

      if (sourceBounds) {
        const width = sourceBounds.width;
        const height = sourceBounds.height;
        const position = putAtDirection(
          existingBounds,
          sourceBounds,
          {width, height},
          direction,
        );
        const newScreenId = await createScreen(
          position.x,
          position.y,
          screenOptions,
        );

        // Pre-populate streaming atom with source genType
        // This ensures FrameNode renders with correct dimensions initially
        // and Input.tsx uses correct currentGenType for detection
        setStreamingGenType({screenId: newScreenId, genType: sourceGenType});

        return newScreenId;
      }

      // Fallback to free-space behavior if no reference position found
      return createScreenInFreeSpace(screenOptions);
    },
    [
      genType,
      createScreen,
      createScreenInFreeSpace,
      fetchHiddenMessageForScreen,
      trpcUtils,
      setStreamingGenType,
      screensWithDimensions,
      getExistingScreenBounds,
    ],
  );

  const createScreenContextual = useCallback(
    async (direction: Direction = 'right'): Promise<string> => {
      const currentActiveScreenId = getActiveScreenId();
      if (!currentActiveScreenId) {
        return createScreenCentered();
      }
      return createScreenRelativeTo(currentActiveScreenId, direction);
    },
    [getActiveScreenId, createScreenCentered, createScreenRelativeTo],
  );

  const value = useMemo(
    () =>
      isComponents
        ? ({
            createScreen: () => createComponentScreen(),
            createScreenCentered: () => createComponentScreenCentered(),
            createScreenInFreeSpace: () => createComponentScreenInFreeSpace(),
            createScreenContextual: (direction: Direction = 'right') => {
              const currentActiveScreenId = getActiveScreenId();
              return currentActiveScreenId
                ? createComponentScreenRelativeTo(
                    currentActiveScreenId,
                    direction,
                  )
                : createComponentScreenCentered();
            },
            createScreenRelativeTo: (
              screenId: string,
              direction: Direction,
              copyOptions?: ComponentScreenCopyOptions,
            ) =>
              createComponentScreenRelativeTo(screenId, direction, copyOptions),
          } satisfies ScreenCreationContextType)
        : {
            createScreen,
            createScreenCentered,
            createScreenInFreeSpace,
            createScreenContextual,
            createScreenRelativeTo,
          },
    [
      isComponents,
      getActiveScreenId,
      createComponentScreen,
      createComponentScreenCentered,
      createComponentScreenInFreeSpace,
      createComponentScreenRelativeTo,
      createScreen,
      createScreenCentered,
      createScreenInFreeSpace,
      createScreenContextual,
      createScreenRelativeTo,
    ],
  );

  return (
    <ScreenCreationContext.Provider value={value}>
      {children}
    </ScreenCreationContext.Provider>
  );
}
